import React, { useCallback, useRef, useState } from 'react';
import {
  ButtonNewRecord,
  FlexRow,
  TableDataState,
  toastFailure,
  toastSuccess,
  usePaginatedQueryV8,
} from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlCreateContributionsForCurrentYear, gqlGetQuery, gqlUpdate } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { useApolloClient, useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';
import { ContributionStates } from 'shared-pages/shared/constants';
import axios from 'axios';
/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const ContributionsGridPage = observer(
  ({ contributorId, isGridInForm = false }: { contributorId?: number; isGridInForm?: boolean }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('MaterialsGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<any>(null);
    const { t } = useTranslation();

    console.log('$$$ contributions - grid - state', location.state, 'isGridInForm=', isGridInForm);

    const { canView, canAddNew, canEdit, canDelete } = useUserLocationPermissions();

    const handleAddNew = () => {};
    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'year DESC,periodId desc,contributor.name';
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      appStore.isPlatformOwner
        ? { statusId: ContributionStates.Saved, contributorId: contributorId }
        : {},
      !isGridInForm, // History Replace
      !isGridInForm ? undefined : 'contributions_grid',
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();

    const canCreateNew = true;
    const handleCreateNew = useCallback(() => {
      history.push({
        pathname: generatePath(getRouteByName('MaterialType'), { id: -1 }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });
    }, [history, location.pathname, location.state]);

    const gridRef = useRef();

    const [mutCreateContributionsForCurrentYear] = useMutation(
      gqlCreateContributionsForCurrentYear,
    );
    const createContributionsForCurrentYear = useCallback(() => {
      mutCreateContributionsForCurrentYear({})
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εισφορών`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }, [handleGraphqlError, refetch, mutCreateContributionsForCurrentYear]);

    const [updateRecord] = useMutation(gqlUpdate);
    const setIsInvoiced = useCallback(
      (id: number, isInvoiced: boolean) => {
        updateRecord({ variables: { data: { id, isInvoiced } } })
          .then((d) => {
            toastSuccess(t(`Επιτυχής Ενημέρωση Κατάστασης Τιμολόγησης`));
            refetch();
          })
          .catch((error) => {
            handleGraphqlError('mutation-update', error);
          });
      },
      [handleGraphqlError, refetch, updateRecord],
    );

    const setReleaseToErp = useCallback(
      async (id: number, releaseToErp: boolean) => {
        if (releaseToErp === false) {
          toastFailure(t(`Δεν επιτρέπεται να ακυρωθεί το Διαθέσιμη στο ERP`));
        } else {
          appStore.setBusy();
          toastSuccess(t(`Αποστολή Στοιχείων προς το ERP`));
          const url = process.env.VITE_APP_WEBAPI.replace('/graphql', `/cnt2erp/${id}`);
          await axios
            .post(url, { id: id })
            .then((res) => {
              console.log('ReleaseToErp response= ', res);
              if (res.data.action === 'ok') {
                updateRecord({ variables: { data: { id, releaseToErp } } })
                  .then((d) => {
                    toastSuccess(t(`Επιτυχής Ενημέρωση Διαθέσιμη στο ERP`));
                  })
                  .catch((error) => {
                    handleGraphqlError('mutation-update', error);
                  });
              } else {
                console.error('ReleaseToErp response= ', res.data.erpResponse.Error);
                toastFailure(t('Παρουσιάστηκε κάποιο λάθος') + ' ' + res.data.erpResponse.Error);
              }
              refetch();
            })
            .catch((error) => {
              handleGraphqlError('mutation-update', error);
              // refetch();
            });
          appStore.clearBusy();
        }
      },
      [handleGraphqlError, refetch, updateRecord],
    );

    /**
     * Render
     */
    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          <Card.Header>
            <FlexRow>
              <Card.Title className="m-0">{t('Εισφορές')}</Card.Title>
              {appStore.isPlatformOwner && (
                <ButtonNewRecord
                  onClick={createContributionsForCurrentYear}
                  size="sm"
                  label={`${t('Δημιουργία Εισφορών')} ${new Date().getFullYear()}`}
                />
              )}
            </FlexRow>
          </Card.Header>
          <Card.Body className="nopadding">
            {/* Φίλτρα */}
            <Filters
              onChange={filtersChanged}
              initialValues={queryState?.filterValues ?? {}}
              disable={tableStore.state !== TableDataState.browse}
              isGridInForm={isGridInForm}
            />

            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t, appStore);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                if (!isGridInForm) {
                  history.replace({
                    pathname: location.pathname,
                    state: {
                      ...location.state,
                      order: orderBy,
                    },
                  });
                }
              }}
              selected={selected}
              setSelected={setSelected}
              refetch={refetch}
              setIsInvoiced={setIsInvoiced}
              setReleaseToErp={setReleaseToErp}
              isGridInForm={isGridInForm}
            />
          </Card.Body>
        </Card>
      );
    }
  },
);

